import { useEffect, useRef, useState, type ReactNode } from 'react';
import { alignRect, stackOrder } from '../../lib/regionGeometry.js';
import { DND_MODULE, LayoutCanvas } from './canvas.js';
import { Tool } from './MobileEditor.js';
import {
  ALIGNMENTS,
  CanvasSettings,
  IssueList,
  PlaylistList,
  PositionFields,
  RegionsList,
  WidgetDetail,
  WidgetTiles,
  checkTitle,
  readWidgetDrag,
  regionTitle,
} from './panels.js';
import { moduleIcon, moduleLabel } from './widgets.js';
import type { LayoutEditorState } from './useLayoutEditor.js';

type PanelKind = 'region' | 'widgets' | 'layers' | 'canvas' | 'check';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const clampZoom = (z: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 100) / 100));

/** Whether a key event is aimed at a text field, where editor shortcuts must stay out of the way. */
function typing(target: EventTarget | null): boolean {
  const t = target as HTMLElement | null;
  return Boolean(t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)));
}

/**
 * The wide-screen editor, laid out like a design app: an icon rail and a
 * side panel on the left, the canvas filling the rest with a floating
 * toolbar for the selected region and zoom controls. Widgets can be dragged
 * from the panel straight onto a region.
 */
export function DesktopEditor({ ed }: { ed: LayoutEditorState }): ReactNode {
  const { doc, history, issues, selectedIndex, setSelectedRegion } = ed;
  const [panel, setPanel] = useState<PanelKind | null>('layers');
  const [regionTab, setRegionTab] = useState<'playlist' | 'position'>('playlist');
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const workspace = useRef<HTMLDivElement>(null);

  const region = doc && selectedIndex >= 0 ? doc.regions[selectedIndex] : undefined;
  const ri = selectedIndex;

  // Ctrl/⌘ + wheel (and trackpad pinch, which arrives as one) zooms the canvas.
  useEffect(() => {
    const el = workspace.current;
    if (!el) return;
    const onWheel = (e: WheelEvent): void => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => clampZoom(z * Math.exp(-e.deltaY / 300)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Delete removes the selected region, Ctrl/⌘+D duplicates it, Esc steps back out.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (typing(e.target)) return;
      if (e.key === 'Escape') {
        if (widgetId) setWidgetId(null);
        else if (region) setSelectedRegion(null);
        else return;
      } else if (!region) {
        return;
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        ed.removeRegion(ri);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        ed.duplicateRegion(ri);
      } else {
        return;
      }
      e.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!doc) return null;
  const shown: PanelKind | null = panel === 'region' && !region ? 'layers' : panel;
  const items = region?.playlist.items ?? [];
  const wi = widgetId ? items.findIndex((w) => w.id === widgetId) : -1;
  const widget = wi >= 0 ? items[wi] : undefined;
  const stack = stackOrder(doc.regions);
  const layer = region ? stack.indexOf(region) : -1;

  function select(id: string | null, openPanel: boolean): void {
    if (id !== ed.selectedRegion) setWidgetId(null);
    setSelectedRegion(id);
    // Picking a region on the canvas brings up its settings, unless the
    // widgets panel is open (it is being used to drop widgets in).
    if (id && openPanel && panel !== null && panel !== 'widgets') setPanel('region');
  }

  function togglePanel(kind: PanelKind): void {
    setPanel((p) => (p === kind || (kind === 'layers' && shown === 'layers' && p === 'region') ? null : kind));
  }

  function openWidget(regionIndex: number, id: string): void {
    const target = doc?.regions[regionIndex];
    if (target) setSelectedRegion(target.id);
    setWidgetId(id);
    setRegionTab('playlist');
    setPanel('region');
  }

  function dropOnRegion(index: number, data: DataTransfer): void {
    const target = doc?.regions[index];
    if (!target) return;
    const module = data.getData(DND_MODULE);
    if (module) {
      openWidget(index, ed.addWidget(index, module));
      return;
    }
    const from = readWidgetDrag(data);
    if (from && from.ri !== index) {
      ed.moveWidget(from.ri, from.wi, index, target.playlist.items.length);
      select(target.id, true);
    }
  }

  const status = ed.saving ? 'Saving…' : ed.dirty || !ed.id ? 'Unsaved changes' : 'All changes saved';

  return (
    <div className="d-editor">
      <header className="topbar d-editor-top">
        <div className="topbar-title d-editor-title">
          <button type="button" className="btn ghost" onClick={ed.leave}>
            ‹ Layouts
          </button>
          <input
            className="d-editor-name"
            aria-label="Layout name"
            value={ed.name}
            onChange={(e) => ed.setName(e.target.value)}
          />
          <span className={`small ${ed.dirty || !ed.id ? 'muted' : 'faint'}`}>{status}</span>
        </div>
        <div className="row topbar-actions">
          <button
            type="button"
            className="btn ghost d-icon-btn"
            aria-label="Undo"
            title="Undo (Ctrl+Z)"
            disabled={!history.canUndo}
            onClick={history.undo}
          >
            ↶
          </button>
          <button
            type="button"
            className="btn ghost d-icon-btn"
            aria-label="Redo"
            title="Redo (Ctrl+Shift+Z)"
            disabled={!history.canRedo}
            onClick={history.redo}
          >
            ↷
          </button>
          <button type="button" className="btn" disabled={ed.saving} onClick={() => void ed.save()}>
            Save draft
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={ed.saving}
            title={issues.length > 0 ? 'Fix the issues listed under Check first' : undefined}
            onClick={() => (issues.length > 0 ? setPanel('check') : void ed.publish())}
          >
            Publish
            {issues.length > 0 ? <span className="m-badge">{issues.length}</span> : null}
          </button>
        </div>
      </header>

      <div className="d-editor-body">
        <nav className="d-rail" aria-label="Editor panels">
          <Tool
            className="d-tool"
            icon="▤"
            label="Region"
            disabled={!region}
            active={shown === 'region'}
            onClick={() => togglePanel('region')}
          />
          <Tool className="d-tool" icon="＋" label="Widgets" active={shown === 'widgets'} onClick={() => togglePanel('widgets')} />
          <Tool className="d-tool" icon="☰" label="Layers" active={shown === 'layers'} onClick={() => togglePanel('layers')} />
          <Tool className="d-tool" icon="▭" label="Canvas" active={shown === 'canvas'} onClick={() => togglePanel('canvas')} />
          <Tool
            className="d-tool"
            icon={issues.length ? '!' : '✓'}
            label="Check"
            badge={issues.length}
            active={shown === 'check'}
            onClick={() => togglePanel('check')}
          />
        </nav>

        {shown ? (
          <aside className="d-panel" aria-label="Panel">
            <div className="d-panel-head">
              {shown === 'region' && widget ? (
                <button type="button" className="icon-btn" aria-label="Back to playlist" onClick={() => setWidgetId(null)}>
                  ‹
                </button>
              ) : null}
              <strong className="ellipsis">
                {shown === 'region' && region
                  ? widget
                    ? `${moduleIcon(widget.module)} ${moduleLabel(widget.module, ed.moduleBySlug.get(widget.module))}`
                    : regionTitle(region, ri)
                  : shown === 'widgets'
                    ? 'Widgets'
                    : shown === 'layers'
                      ? 'Layers'
                      : shown === 'canvas'
                        ? 'Canvas'
                        : checkTitle(issues)}
              </strong>
              <button type="button" className="icon-btn" aria-label="Close panel" onClick={() => setPanel(null)}>
                ✕
              </button>
            </div>
            <div className="d-panel-body">
              {shown === 'region' && region ? (
                widget ? (
                  <WidgetDetail ed={ed} ri={ri} wi={wi} onDeleted={() => setWidgetId(null)} />
                ) : (
                  <>
                    <div className="segmented m-full" role="tablist" aria-label="Region settings">
                      {(['playlist', 'position'] as const).map((t) => (
                        <button
                          key={t}
                          type="button"
                          role="tab"
                          aria-selected={regionTab === t}
                          aria-pressed={regionTab === t}
                          onClick={() => setRegionTab(t)}
                        >
                          {t === 'playlist' ? `Playlist · ${items.length}` : 'Position & layer'}
                        </button>
                      ))}
                    </div>
                    {regionTab === 'playlist' ? (
                      <PlaylistList ed={ed} ri={ri} onOpen={setWidgetId} onAdd={() => setPanel('widgets')} draggable />
                    ) : (
                      <PositionFields ed={ed} ri={ri} />
                    )}
                  </>
                )
              ) : null}
              {shown === 'widgets' ? (
                <>
                  <p className="faint small" style={{ margin: 0 }}>
                    {region
                      ? `Click to add to ${regionTitle(region, ri)}, or drag onto any region.`
                      : 'Drag a widget onto a region, or select a region and click.'}
                  </p>
                  <WidgetTiles ed={ed} draggable onPick={region ? (m) => openWidget(ri, ed.addWidget(ri, m)) : undefined} />
                </>
              ) : null}
              {shown === 'layers' ? <RegionsList ed={ed} onSelect={(id) => select(id, false)} /> : null}
              {shown === 'canvas' ? <CanvasSettings ed={ed} /> : null}
              {shown === 'check' ? <IssueList issues={issues} /> : null}
            </div>
          </aside>
        ) : null}

        <div ref={workspace} className="d-workspace">
          {region ? (
            <div className="d-context" role="toolbar" aria-label="Region tools">
              <strong className="ellipsis d-context-name">{regionTitle(region, ri)}</strong>
              <span className="d-sep" />
              {ALIGNMENTS.map((a) => (
                <button
                  key={a.align}
                  type="button"
                  className="d-icon"
                  title={`Align ${a.label.toLowerCase()}`}
                  aria-label={`Align ${a.label.toLowerCase()}`}
                  onClick={() => ed.patchRegion(ri, { rect: alignRect(region.rect, a.align, doc.dimensions) })}
                >
                  {a.icon}
                </button>
              ))}
              <button
                type="button"
                className="d-icon"
                title="Fill the whole screen"
                aria-label="Fill the whole screen"
                onClick={() => ed.patchRegion(ri, { rect: alignRect(region.rect, 'fill', doc.dimensions) })}
              >
                ⛶
              </button>
              <span className="d-sep" />
              <button
                type="button"
                className="d-icon"
                title="Send backward"
                aria-label="Send backward"
                disabled={layer <= 0}
                onClick={() => ed.restackRegion(region.id, 'backward')}
              >
                ↓
              </button>
              <button
                type="button"
                className="d-icon"
                title="Bring forward"
                aria-label="Bring forward"
                disabled={layer >= stack.length - 1}
                onClick={() => ed.restackRegion(region.id, 'forward')}
              >
                ↑
              </button>
              <span className="d-sep" />
              <button
                type="button"
                className="d-text-btn"
                onClick={() => {
                  setWidgetId(null);
                  setRegionTab('position');
                  setPanel('region');
                }}
              >
                Position
              </button>
              <button
                type="button"
                className="d-icon"
                title="Duplicate (Ctrl+D)"
                aria-label="Duplicate region"
                onClick={() => ed.duplicateRegion(ri)}
              >
                ⧉
              </button>
              <button
                type="button"
                className="d-icon danger"
                title="Delete (Del)"
                aria-label="Delete region"
                disabled={doc.regions.length <= 1}
                onClick={() => ed.removeRegion(ri)}
              >
                🗑
              </button>
            </div>
          ) : null}

          <LayoutCanvas
            doc={doc}
            media={ed.media}
            selectedId={ed.selectedRegion}
            onSelect={(id) => select(id, true)}
            onRectChange={(index, rect, key) => ed.patchRegion(index, { rect }, key)}
            onGestureStart={history.begin}
            onGestureEnd={history.end}
            onDropData={dropOnRegion}
            zoom={zoom}
          />

          <p className="d-hint faint small">
            {doc.dimensions.width}×{doc.dimensions.height} · drag to move · arrows nudge · Alt skips snapping
          </p>
          <div className="d-zoom" role="group" aria-label="Zoom">
            <button type="button" className="d-icon" aria-label="Zoom out" onClick={() => setZoom((z) => clampZoom(z / 1.25))}>
              −
            </button>
            <button type="button" className="d-text-btn" title="Fit to screen" onClick={() => setZoom(1)}>
              {zoom === 1 ? 'Fit' : `${Math.round(zoom * 100)}%`}
            </button>
            <button type="button" className="d-icon" aria-label="Zoom in" onClick={() => setZoom((z) => clampZoom(z * 1.25))}>
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
